import React, {
  useContext,
  useRef,
  useEffect,
  useState,
} from "react";
import "./CSS/sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../img/about-nbg.png";
import { SidebarContext } from "../SidebarContext";
import { motion } from "framer-motion";
import { FaBars, FaTimes, FaPlus, FaPen, FaTrash, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../config/firebase-config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useConversationList } from "../hooks/useConversationList";
import { deleteConversation, updateTitle } from "../services/conversationService";

function useDimensions(ref) {
  const dimensions = React.useRef({ width: 0, height: 0 });

  useEffect(() => {
    if (ref.current) {
      dimensions.current.width = ref.current.offsetWidth;
      dimensions.current.height = ref.current.offsetHeight;
    }
  }, [ref]);

  return dimensions.current;
}

// Circle clip-path variants
const sidebarVariants = {
  open: (height = 1000) => ({
    clipPath: `circle(${(height * 2) + 30}px at 40px 40px)`,
    transition: {
      type: "spring",
      stiffness: 20,
      restDelta: 2,
    },
  }),
  closed: {
    clipPath: "circle(0px at 40px 40px)",
    transition: {
      delay: 0.1,
      type: "spring",
      stiffness: 400,
      damping: 40,
    },
  },
};

// Sub-buttons stagger animation
const subButtonsVariants = {
  open: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 },
  },
};

// Each menu item fade/slide in
const menuItemVariants = {
  open: { opacity: 1, x: 0 },
  closed: { opacity: 0, x: -20 },
};

// Conversation item component
const ConversationItem = ({ conv, isActive, onSelect, onRename, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conv.title || 'New Conversation');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== conv.title) {
      onRename(conv.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditTitle(conv.title || 'New Conversation');
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`conv-item ${isActive ? 'conv-item-active' : ''}`}
      onClick={() => !isEditing && onSelect(conv)}
    >
      {isEditing ? (
        <div className="conv-item-edit">
          <input
            ref={inputRef}
            className="conv-item-input"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
          />
        </div>
      ) : (
        <>
          <div className="conv-item-title">{conv.title || 'New Conversation'}</div>
          <div className="conv-item-actions">
            <button
              className="conv-action-btn"
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              title="Rename"
            >
              <FaPen size={10} />
            </button>
            <button
              className="conv-action-btn conv-action-delete"
              onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
              title="Delete"
            >
              <FaTrash size={10} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Sidebar = ({ hasNavbar, accountType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar } = useContext(SidebarContext);
  const [assistantsOpen, setAssistantsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [userMemory, setUserMemory] = useState('');
  const [memoryInput, setMemoryInput] = useState('');
  const [memorySaving, setMemorySaving] = useState(false);
  const [user] = useAuthState(auth);
  const { grouped, loading: convsLoading } = useConversationList(user);

  // Get the current conversationId from the URL
  const pathParts = location.pathname.split('/');
  const currentConvId = pathParts[1] === 'chat' && pathParts[3] ? pathParts[3] : null;

  // Measure the sidebar's height for the circle radius
  const containerRef = useRef(null);
  const { height } = useDimensions(containerRef);

  // Load user memory from Firestore
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const mem = snap.exists() ? (snap.data().memory || '') : '';
        setUserMemory(mem);
        setMemoryInput(mem);
      } catch (err) {
        console.error('Failed to load memory:', err);
      }
    })();
  }, [user]);

  const handleSaveMemory = async () => {
    if (!user) return;
    setMemorySaving(true);
    try {
      await setDoc(doc(db, 'users', user.uid), { memory: memoryInput }, { merge: true });
      setUserMemory(memoryInput);
      setShowMemoryModal(false);
    } catch (err) {
      console.error('Failed to save memory:', err);
    } finally {
      setMemorySaving(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    window.location.reload();
  };

  const handleNewChat = () => {
    // Navigate to the current assistant type (or default) without a conversationId
    const currentChatType = pathParts[1] === 'chat' && pathParts[2] ? pathParts[2] : 'nurAlHuda';
    navigate(`/chat/${currentChatType}`);
    window.location.reload();
  };

  const handleSelectConversation = (conv) => {
    navigate(`/chat/${conv.assistantType}/${conv.id}`);
    window.location.reload();
  };

  const handleRenameConversation = async (convId, newTitle) => {
    if (!user) return;
    try {
      await updateTitle(user.uid, convId, newTitle);
    } catch (err) {
      console.error('Failed to rename conversation:', err);
    }
  };

  const handleDeleteConversation = async (convId) => {
    if (!user) return;
    try {
      await deleteConversation(user.uid, convId);
      // If we're viewing the deleted conversation, navigate away
      if (currentConvId === convId) {
        navigate('/chat/nurAlHuda');
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  // Menu items
  const assistants = [
    { name: "Nur Al Huda", path: "/chat/nurAlHuda" },
    { name: "Nur Al Huda For Kids", path: "/chat/nurAlHudaForKids" },
    { name: "Islamic Socratic Method", path: "/chat/islamicSocraticMethod" },
    { name: "Iqra With Us", path: "/chat/iqraWithUs" },
    { name: "Nur Tutor", path: "/chat/nurTutor" },
    { name: "PaliGPT", path: "/chat/paliGPT" },
  ];

  const tools = [
    { name: "GraderBot", path: "/tools/graderbot" },
    { name: "Quiz Generator", path: "/tools/quiz-generator" },
    { name: "5D Lesson Planner", path: "/tools/5dthinking" },
    { name: "Presentation Generator", path: "/tools/slidegenerator" },
    { name: "Flashcards", path: "/tools/flashcards" },
  ];

  const dateGroups = ['Today', 'Yesterday', 'Previous 7 Days', 'Older'];
  const hasConversations = Object.values(grouped).some(g => g.length > 0);

  return (
    <div
      className={`sidebar-container ${isSidebarOpen ? "open" : "closed"} ${
        hasNavbar ? "with-navbar" : ""
      }`}
    >
      <motion.div
        className="sidebar"
        ref={containerRef}
        initial={false}
        animate={isSidebarOpen ? "open" : "closed"}
        custom={height}
        variants={sidebarVariants}
      >
        <motion.div
          className="sidebar-bg"
          animate={{
            opacity: isSidebarOpen ? 1 : 0,
            transition: { duration: 0.2 }
          }}
        />

        {/* Sidebar Content */}
        <div className="sidebar-content">
          {/* Brand: Always Visible */}
          <div className="dashbrand-container">
            <a href="/">
              <img src={logo} alt="Nur Al Huda" className="dashbrand-logo" />
              <div>
                <div className="dashbrand-title">Nur Al Huda</div>
                <div className="dashbrand-subtitle">AI for Islamic Research</div>
              </div>
            </a>
          </div>

          {/* New Chat Button */}
          {user && (
            <button className="new-chat-btn" onClick={handleNewChat}>
              <FaPlus size={12} />
              <span>New Chat</span>
            </button>
          )}

          <div className="main-buttons">
            <div
              className="main-button section-header"
              onClick={() => setAssistantsOpen((o) => !o)}
            >
              Assistants
              <span className="section-chevron">
                {assistantsOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
              </span>
            </div>
            {assistantsOpen && (
              <motion.div
                className="sub-buttons"
                variants={subButtonsVariants}
                initial={false}
                animate={isSidebarOpen ? "open" : "closed"}
              >
                {assistants.map((assistant) => (
                  <motion.button
                    key={assistant.name}
                    className="sub-button"
                    onClick={() => handleNavigation(assistant.path)}
                    variants={menuItemVariants}
                  >
                    {assistant.name}
                  </motion.button>
                ))}
              </motion.div>
            )}

            {accountType === "enterprise" && (
              <div>
                <div
                  className="main-button section-header"
                  onClick={() => setToolsOpen((o) => !o)}
                >
                  Tools
                  <span className="section-chevron">
                    {toolsOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                  </span>
                </div>
                {toolsOpen && (
                  <motion.div
                    className="sub-buttons"
                    variants={subButtonsVariants}
                    initial={false}
                    animate={isSidebarOpen ? "open" : "closed"}
                  >
                    {tools.map((tool) => (
                      <motion.button
                        key={tool.name}
                        className="sub-button"
                        onClick={() => handleNavigation(tool.path)}
                        variants={menuItemVariants}
                      >
                        {tool.name}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Conversation History — Recents */}
          {user && hasConversations && (
            <div className="recents-section">
              <div className="recents-divider" />
              <div className="recents-label">Recents</div>
              <motion.div
                className="recents-list"
                variants={subButtonsVariants}
                initial={false}
                animate={isSidebarOpen ? "open" : "closed"}
              >
                {dateGroups.map((group) => {
                  const convs = grouped[group];
                  if (!convs || convs.length === 0) return null;
                  return (
                    <div key={group} className="recents-group">
                      <div className="recents-group-label">{group}</div>
                      {convs.map((conv) => (
                        <motion.div key={conv.id} variants={menuItemVariants}>
                          <ConversationItem
                            conv={conv}
                            isActive={currentConvId === conv.id}
                            onSelect={handleSelectConversation}
                            onRename={handleRenameConversation}
                            onDelete={handleDeleteConversation}
                          />
                        </motion.div>
                      ))}
                    </div>
                  );
                })}
              </motion.div>
            </div>
          )}

          <div className="footer-buttons">
            <a
              href="https://billing.stripe.com/p/login/14kfYZ43Qa1L63K3cc"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-button"
            >
              Customer Portal
            </a>
            <button
              className="footer-button"
              onClick={() => navigate("/terms-of-use")}
            >
              Terms of Use
            </button>
            <button
              className="footer-button"
              onClick={() => navigate("/privacy-policy")}
            >
              Privacy Policy
            </button>
            <button
              className="footer-button"
              onClick={() => navigate("/contact-form")}
            >
              Contact Us
            </button>
            {user && (
              <button
                className="footer-button memory-btn"
                onClick={() => { setMemoryInput(userMemory); setShowMemoryModal(true); }}
              >
                {userMemory ? 'Edit Memory' : '+ Add Memory'}
              </button>
            )}
            <p className="footer-text">&copy; 2024 Nur Al Huda. All rights reserved.</p>
          </div>
        </div>
      </motion.div>

      {/* Drawer handle */}
      <div className="drawer-handle" onClick={toggleSidebar}>
        <div className="drawer-icon">
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </div>
      </div>

      {/* Memory Modal */}
      {showMemoryModal && (
        <div className="memory-modal-overlay" onClick={() => setShowMemoryModal(false)}>
          <div className="memory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="memory-modal-header">
              <h3>Memory</h3>
              <button className="memory-modal-close" onClick={() => setShowMemoryModal(false)}>
                <FaTimes />
              </button>
            </div>
            <p className="memory-modal-desc">
              This text is added as context at the start of every new conversation to help the AI understand you better.
            </p>
            <textarea
              className="memory-modal-textarea"
              value={memoryInput}
              onChange={(e) => setMemoryInput(e.target.value)}
              placeholder="e.g. I am a Grade 7 teacher at Al Iman School. I focus on Islamic Studies and Quran. My students are 12–13 years old."
              rows={6}
            />
            <div className="memory-modal-actions">
              <button className="memory-btn-cancel" onClick={() => setShowMemoryModal(false)}>
                Cancel
              </button>
              <button
                className="memory-btn-save"
                onClick={handleSaveMemory}
                disabled={memorySaving}
              >
                {memorySaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
